import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { EXTERNE_CONTACTEN_API } from "../lib/contactApiPaths";
import type { Contact, Group, User } from "@domaddress/shared";
import { isOrgContactReadOnlyRole } from "@domaddress/shared";
import ContactCard from "../components/ContactCard";
import ContactListRow from "../components/ContactListRow";
import ContactFormModal from "../components/ContactFormModal";
import ContactViewModal from "../components/ContactViewModal";
import ViewModeToggle from "../components/ViewModeToggle";
import PaginationBar from "../components/PaginationBar";
import { useViewMode } from "../hooks/useViewMode";
import {
  useVcardEmailSend,
  VcardEmailToolbarButton,
  VcardEmailAside,
} from "../components/VcardEmailSendParts";

export default function ExterneContactenPage({ user = null }: { user?: User | null }) {
  const readOnly = user ? isOrgContactReadOnlyRole(user) : false;
  const [viewMode, setViewMode] = useViewMode("externe");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [viewing, setViewing] = useState<Contact | null>(null);
  const [groupFilter, setGroupFilter] = useState("");
  const [groups, setGroups] = useState<Group[]>([]);
  const [emailFilter, setEmailFilter] = useState<"" | "without" | "with">("");

  const vcard = useVcardEmailSend(EXTERNE_CONTACTEN_API);

  useEffect(() => {
    api.get("/api/groups").then((r) => setGroups(r.data.data));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, groupFilter, cityFilter, countryFilter, perPage, emailFilter]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(page),
        per_page: String(perPage),
      };
      if (search.trim()) params.search = search.trim();
      if (groupFilter) params.group_id = groupFilter;
      if (cityFilter.trim()) params.city = cityFilter.trim();
      if (countryFilter.trim()) params.country = countryFilter.trim();
      if (emailFilter) params.has_email = emailFilter;
      const res = await api.get(EXTERNE_CONTACTEN_API, { params });
      setContacts(res.data.data);
      setTotal(typeof res.data.total === "number" ? res.data.total : res.data.data.length);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, groupFilter, cityFilter, countryFilter, emailFilter]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  async function handleDelete(id: number) {
    if (!confirm("Dit externe contact verwijderen?")) return;
    await api.delete(`${EXTERNE_CONTACTEN_API}/${id}`);
    await fetchContacts();
  }

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageOutOfRange = page > totalPages && total > 0;
  useEffect(() => {
    if (pageOutOfRange) setPage(totalPages);
  }, [pageOutOfRange, totalPages, page]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Externe contacten</h2>
          <p className="page-subtitle">
            {loading ? "…" : `${total} contact${total !== 1 ? "en" : ""}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {user && <VcardEmailToolbarButton b={vcard} />}
          {!readOnly && (
            <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
              <span>+</span> Nieuw extern contact
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" as const }}>
        <div style={{ flex: 1, minWidth: 0 }}>
      <div className="filters">
        <div className="filters-start">
          <div className="search-box">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Zoeken (naam, tussenvoegsel…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <select
            className="filter-select"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
          >
            <option value="">Alle groepen</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <input
            type="text"
            className="filter-select"
            style={{ minWidth: "120px" }}
            placeholder="Woonplaats bevat…"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            aria-label="Filter by city"
          />
          <input
            type="text"
            className="filter-select"
            style={{ minWidth: "120px" }}
            placeholder="Land bevat…"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            aria-label="Filter by country"
          />
          <select
            className="filter-select"
            aria-label="Filter op e-mailadres"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value as "" | "without" | "with")}
          >
            <option value="">E-mail: alle</option>
            <option value="without">Zonder e-mail</option>
            <option value="with">Met e-mail</option>
          </select>
        </div>
        <div className="filters-end">
          <ViewModeToggle
            value={viewMode}
            onChange={setViewMode}
            label="Weergave"
            tilesLabel="Tegels"
            listLabel="Lijst"
          />
        </div>
      </div>

      {loading ? (
        viewMode === "tiles" ? (
          <div className="loading-grid">
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton-card" />)}
          </div>
        ) : (
          <div className="loading-list">
            {[...Array(8)].map((_, i) => <div key={i} className="skeleton-card" />)}
          </div>
        )
      ) : total === 0 ? (
        <div className="empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <p>Geen externe contacten</p>
          {!readOnly && (
            <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
              Eerste contact toevoegen
            </button>
          )}
        </div>
      ) : (
        <>
          {contacts.length === 0 ? (
            <div className="empty-state">
              <p>Geen resultaten op deze pagina. Wijzig filters of ga naar de eerste pagina.</p>
              <button type="button" className="btn-secondary" onClick={() => setPage(1)}>
                Eerste pagina
              </button>
            </div>
          ) : viewMode === "tiles" ? (
            <div className="contacts-grid">
              {contacts.map((contact) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  onView={() => setViewing(contact)}
                  onEdit={readOnly ? undefined : () => { setEditing(contact); setShowForm(true); }}
                  onDelete={readOnly ? undefined : () => handleDelete(contact.id)}
                  onAddToSendList={
                    vcard.sendListOpen ? () => vcard.addToSendList(contact) : undefined
                  }
                />
              ))}
            </div>
          ) : (
            <div className="data-list">
              {contacts.map((contact) => (
                <ContactListRow
                  key={contact.id}
                  contact={contact}
                  onView={() => setViewing(contact)}
                  onEdit={readOnly ? undefined : () => { setEditing(contact); setShowForm(true); }}
                  onDelete={readOnly ? undefined : () => handleDelete(contact.id)}
                  onAddToSendList={
                    vcard.sendListOpen ? () => vcard.addToSendList(contact) : undefined
                  }
                />
              ))}
            </div>
          )}
          <PaginationBar
            page={page}
            perPage={perPage}
            total={total}
            itemLabel="contacten"
            onPageChange={setPage}
            onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
          />
        </>
      )}
        </div>
        <VcardEmailAside b={vcard} />
      </div>

      {viewing && (
        <ContactViewModal
          contact={viewing}
          user={user}
          apiPath={EXTERNE_CONTACTEN_API}
          title="Extern contact"
          showEditButton={!readOnly}
          onClose={() => setViewing(null)}
          onEdit={
            readOnly
              ? undefined
              : (c) => {
                  setViewing(null);
                  setEditing(c);
                  setShowForm(true);
                }
          }
        />
      )}

      {showForm && (
        <ContactFormModal
          apiPath={EXTERNE_CONTACTEN_API}
          titleNieuw="Nieuw extern contact"
          titleBewerken="Extern contact bewerken"
          contact={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchContacts(); }}
        />
      )}

    </div>
  );
}
